import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useSession } from "next-auth/react";
import {
  readSessionCache,
  removeSessionCache,
  writeSessionCache,
} from "../utils/sessionCache";

export type Notification = {
  _id: string;
  type:
    | "new_comment"
    | "new_reply"
    | "new_like"
    | "new_content"
    | "new_franchise_content"
    | "game_published";
  message: string;
  triggeredByName?: string;
  triggeredByPicture?: string;
  contentId?: string;
  contentTitle?: string;
  contentThumbnail?: string;
  gameId?: string;
  gameTitle?: string;
  link: string;
  isRead: boolean;
  createdAt: string;
};

type NotificationsContextType = {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (id: string) => void;
  loadMore: () => void;
  refresh: () => void;
};

const NotificationsContext = createContext<NotificationsContextType>({
  notifications: [],
  unreadCount: 0,
  loading: false,
  loadingMore: false,
  hasMore: false,
  markAsRead: () => {},
  markAllAsRead: () => {},
  deleteNotification: () => {},
  loadMore: () => {},
  refresh: () => {},
});

// The poll only asks for the unread count (one integer). The list itself is
// fetched on demand — NotificationDropdown on open, NotificationsList on
// mount — through refresh().
const POLL_INTERVAL = 90_000; // 90 seconds
const PAGE_SIZE = 20;
const cacheKeyFor = (userId?: string) =>
  userId ? `gow:notif-unread:${userId}` : null;

export const NotificationsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { data: session } = useSession();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // grows on loadMore; refresh always re-fetches everything loaded so far
  const sizeRef = useRef(PAGE_SIZE);
  const hasLoadedRef = useRef(false);
  // True once the badge has a real value (cache or network) for this token,
  // so the write-through below never persists the pre-seed 0.
  const seededRef = useRef(false);
  const lastCacheKeyRef = useRef<string | null>(null);

  const token = session?.user?.accessToken;
  const cacheKey = cacheKeyFor(session?.user?._id);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      if (!hasLoadedRef.current) setLoading(true);
      const res = await fetch(
        `${process.env.BACKEND_BASE_URL}/notifications?size=${sizeRef.current}&page=1`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await res.json();
      if (data.success) {
        const items = data.data.notifications || [];
        setNotifications(items);
        setUnreadCount(data.data.unreadCount || 0);
        setTotal(data.data.total ?? items.length);
        hasLoadedRef.current = true;
        seededRef.current = true;
      }
    } catch {
      // silently fail — polling should not disrupt the user
    } finally {
      setLoading(false);
    }
  }, [token]);

  // GET /notifications/unread-count → { success, data: { count } }
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(
        `${process.env.BACKEND_BASE_URL}/notifications/unread-count`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await res.json();
      if (data.success && typeof data.data?.count === "number") {
        setUnreadCount(data.data.count);
        seededRef.current = true;
        // A verified value always refreshes the cache stamp.
        if (cacheKey) writeSessionCache(cacheKey, data.data.count);
      }
    } catch {
      // silently fail — polling should not disrupt the user
    }
  }, [token, cacheKey]);

  // Write-through for every count change (list fetch, mark-read, delete).
  // Declared before the loader so it never runs with seededRef already true
  // and the pre-seed 0 still in state. An unchanged value is not re-stamped:
  // that would keep a stale entry "fresh" across quick reloads and skip the
  // network indefinitely.
  useEffect(() => {
    if (!token || !cacheKey || !seededRef.current) return;
    if (readSessionCache<number>(cacheKey)?.value === unreadCount) return;
    writeSessionCache(cacheKey, unreadCount);
  }, [unreadCount, token, cacheKey]);

  // Seed the badge, then poll the count; pause while the tab is hidden
  useEffect(() => {
    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      setTotal(0);
      sizeRef.current = PAGE_SIZE;
      hasLoadedRef.current = false;
      seededRef.current = false;
      if (lastCacheKeyRef.current) {
        removeSessionCache(lastCacheKeyRef.current);
        lastCacheKeyRef.current = null;
      }
      return;
    }
    lastCacheKeyRef.current = cacheKey;

    // Paint the last known count immediately; only hit the network when the
    // cached value is older than one poll interval.
    const cached = cacheKey ? readSessionCache<number>(cacheKey) : undefined;
    if (cached && typeof cached.value === "number") {
      setUnreadCount(cached.value);
      seededRef.current = true;
    }
    if (!cached || Date.now() - cached.at >= POLL_INTERVAL) fetchUnreadCount();

    const stopPolling = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    const startPolling = () => {
      stopPolling();
      timerRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL);
    };
    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchUnreadCount();
        startPolling();
      }
    };

    startPolling();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [token, cacheKey, fetchUnreadCount]);

  const loadMore = useCallback(async () => {
    sizeRef.current += PAGE_SIZE;
    setLoadingMore(true);
    await fetchNotifications();
    setLoadingMore(false);
  }, [fetchNotifications]);

  const markAsRead = useCallback(
    async (id: string) => {
      if (!token) return;
      // Optimistic update
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));

      await fetch(
        `${process.env.BACKEND_BASE_URL}/notifications/${id}/read`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      ).catch(() => {});
    },
    [token]
  );

  const markAllAsRead = useCallback(async () => {
    if (!token) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);

    await fetch(
      `${process.env.BACKEND_BASE_URL}/notifications/read-all`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    ).catch(() => {});
  }, [token]);

  const deleteNotification = useCallback(
    async (id: string) => {
      if (!token) return;
      const wasUnread = notifications.find((n) => n._id === id)?.isRead === false;
      setNotifications((prev) => prev.filter((n) => n._id !== id));
      setTotal((prev) => Math.max(0, prev - 1));
      if (wasUnread) setUnreadCount((prev) => Math.max(0, prev - 1));

      await fetch(
        `${process.env.BACKEND_BASE_URL}/notifications/${id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      ).catch(() => {});
    },
    [token, notifications]
  );

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        loadingMore,
        hasMore: notifications.length < total,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        loadMore,
        refresh: fetchNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationsContext);
